import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

// Local type — mirrors MealSlot shape we need, avoids cross-feature type import
interface MealSlotLike {
  dayOfWeek: number
  recipeId: string | null
}

interface WeeklyMealPlanLike {
  slots: MealSlotLike[]
}

interface WeekSummaryProps {
  plan: WeeklyMealPlanLike
  onGenerate: () => void
  isGenerating: boolean
  isPlanner: boolean
}

const DAY_INDICES = [0, 1, 2, 3, 4, 5, 6] as const

export function WeekSummary({ plan, onGenerate, isGenerating, isPlanner }: WeekSummaryProps) {
  const { t } = useTranslation('home')

  const filledSlots = plan.slots.filter(s => s.recipeId !== null)
  const filledCount = filledSlots.length

  return (
    <div className="flex flex-col items-center gap-6 px-4 py-8 max-w-md mx-auto">
      {/* Mini 7-day grid */}
      <div className="flex gap-3" role="img" aria-label={t('weekSummary.daysPlanned', { filled: filledCount })}>
        {DAY_INDICES.map(day => {
          const slot = plan.slots.find(s => s.dayOfWeek === day)
          const isFilled = slot?.recipeId !== null && slot?.recipeId !== undefined

          return (
            <div key={day} className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'h-10 w-10 rounded-full flex items-center justify-center text-xs font-medium',
                  isFilled
                    ? 'bg-primary text-white'
                    : 'border-2 border-dashed border-muted text-muted',
                )}
                aria-hidden="true"
              >
                {t(`weekSummary.dayLabels.${day}`)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary text */}
      <p className="text-sm text-muted">
        {t('weekSummary.daysPlanned', { filled: filledCount })}
      </p>

      {/* Generate CTA or waiting message */}
      {isPlanner ? (
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGenerating}
          className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-white disabled:opacity-50 w-full max-w-xs"
        >
          {t('weekSummary.generateList')}
        </button>
      ) : (
        <p className="text-sm text-muted italic">
          {t('weekSummary.waitingForList')}
        </p>
      )}
    </div>
  )
}
